const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

export function downloadReport({ name, location, soil = {}, analysis = {} }) {
  const health = analysis.soil_health || {};
  const crops = analysis.crops || [];
  const ferts = analysis.fertilizers || [];
  const treatments = analysis.treatments || [];
  const tips = analysis.tips || [];
  const score = Math.round(health.score || 0);
  const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  const row = (label, val) => `<div class="cell"><span class="k">${label}</span><span class="v">${esc(val)}</span></div>`;

  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const norm = (a) => (Array.isArray(a) ? a.map(Number).filter((m) => m >= 1 && m <= 12) : []);
  const calRows = crops.filter((c) => c.calendar && (norm(c.calendar.sow_months).length || norm(c.calendar.harvest_months).length));
  const calendarHtml = calRows.length ? `
    <h2>Planting Calendar</h2>
    <div class="legend"><span><i class="sw sow"></i> Sow</span><span><i class="sw har"></i> Harvest</span><span><i class="sw both"></i> Both</span></div>
    <table class="cal"><thead><tr><th></th>${MONTHS.map((m) => `<th>${m}</th>`).join("")}</tr></thead><tbody>
    ${calRows.map((c) => {
      const sow = new Set(norm(c.calendar.sow_months)), har = new Set(norm(c.calendar.harvest_months));
      return `<tr><td class="cname">${esc(c.name)}${c.calendar.note ? `<div class="note">${esc(c.calendar.note)}</div>` : ""}</td>
        ${MONTHS.map((_, i) => { const s = sow.has(i + 1), h = har.has(i + 1); return `<td><div class="sw ${s && h ? "both" : s ? "sow" : h ? "har" : "none"}"></div></td>`; }).join("")}</tr>`;
    }).join("")}
    </tbody></table>` : "";

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>AgroTerra Soil Report — ${esc(name)}</title>
  <style>
    @page { margin: 24mm 18mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Helvetica, Arial, sans-serif; color: #1c1917; margin: 0; }
    .brand { display:flex; align-items:center; gap:10px; border-bottom:3px solid #2D6A4F; padding-bottom:12px; margin-bottom:20px; }
    .logo { width:36px; height:36px; border-radius:9px; background:#2D6A4F; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:800; }
    h1 { font-size:22px; margin:0; color:#143D29; }
    .sub { color:#57534e; font-size:13px; }
    h2 { font-size:15px; color:#143D29; border-left:4px solid #52B788; padding-left:8px; margin:24px 0 10px; }
    .score { display:flex; align-items:center; gap:16px; background:#EAF6EF; border-radius:12px; padding:16px; }
    .score .num { font-size:40px; font-weight:800; color:#2D6A4F; }
    .badge { display:inline-block; background:#2D6A4F; color:#fff; font-size:11px; text-transform:uppercase; letter-spacing:1px; padding:3px 10px; border-radius:20px; }
    .grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:8px; }
    .cell { background:#f7f6f3; border:1px solid #e7e5e4; border-radius:8px; padding:8px 10px; }
    .k { display:block; font-size:10px; text-transform:uppercase; letter-spacing:.5px; color:#78716c; }
    .v { font-weight:700; font-size:15px; }
    .crop { border:1px solid #e7e5e4; border-radius:10px; padding:10px 12px; margin-bottom:8px; }
    .crop .top { display:flex; justify-content:space-between; font-weight:700; color:#143D29; }
    .match { background:#EAF6EF; color:#2D6A4F; font-size:12px; padding:2px 8px; border-radius:6px; }
    ul { margin:6px 0; padding-left:18px; } li { margin:4px 0; font-size:13px; }
    .foot { margin-top:28px; border-top:1px solid #e7e5e4; padding-top:10px; font-size:11px; color:#a8a29e; text-align:center; }
    p { font-size:13px; line-height:1.5; }
    .cal { width:100%; border-collapse:separate; border-spacing:3px; page-break-inside:avoid; }
    .cal th { font-size:10px; color:#78716c; font-weight:600; }
    .cal td { text-align:center; }
    .cname { text-align:left !important; font-size:12px; font-weight:700; color:#143D29; width:120px; }
    .note { font-size:10px; font-weight:400; color:#78716c; }
    .sw { display:inline-block; width:100%; min-width:14px; height:18px; border-radius:4px; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    .sw.sow { background:#D97706; } .sw.har { background:#2D6A4F; } .sw.both { background:linear-gradient(90deg,#D97706,#2D6A4F); } .sw.none { background:#f1f0ed; }
    .legend { display:flex; gap:16px; font-size:11px; color:#57534e; margin-bottom:6px; } .legend .sw { width:14px; height:12px; vertical-align:middle; margin-right:4px; }
  </style></head><body>
    <div class="brand"><div class="logo">A</div><div><h1>AgroTerra Soil Report</h1><div class="sub">${esc(name)}${location ? " · " + esc(location) : ""} · ${date}</div></div></div>

    <div class="score"><div class="num">${score}<span style="font-size:16px;color:#a8a29e">/100</span></div>
      <div><span class="badge">${esc(health.status || "—")}</span><p style="margin:6px 0 0">${esc(health.summary || "")}</p></div>
    </div>

    <h2>Soil Properties</h2>
    <div class="grid">
      ${row("pH", soil.ph)}${row("Soil Type", soil.soil_type)}${row("Moisture", (soil.moisture ?? "") + "%")}
      ${row("Organic Matter", (soil.organic_matter ?? "") + "%")}${row("Nitrogen (N)", (soil.nitrogen ?? "") + "/100")}
      ${row("Phosphorus (P)", (soil.phosphorus ?? "") + "/100")}${row("Potassium (K)", (soil.potassium ?? "") + "/100")}
    </div>

    <h2>Recommended Crops</h2>
    ${crops.map((c) => `<div class="crop"><div class="top"><span>${esc(c.name)}</span><span class="match">${Math.round(c.match || 0)}% match</span></div>
      <p style="margin:6px 0 0">${esc(c.why)}</p>
      <div class="sub">☀ ${esc(c.sunlight)} · 💧 ${esc(c.water)} · 📈 ${esc(c.yield)} · ${esc(c.profit)} profit</div></div>`).join("")}

    ${calendarHtml}

    <h2>Fertilisers &amp; Products</h2>
    <ul>${ferts.map((f) => `<li><b>${esc(f.name)}</b> — ${esc(f.purpose)} <i>(${esc(f.dosage)})</i></li>`).join("")}</ul>

    <h2>Treatment Plan</h2>
    <ol>${treatments.map((t) => `<li><b>${esc(t.step)}</b> — ${esc(t.detail)}</li>`).join("")}</ol>

    <h2>Farm Tips</h2>
    <ul>${tips.map((t) => `<li>${esc(t)}</li>`).join("")}</ul>

    <div class="foot">Generated by AgroTerra · AI Soil &amp; Crop Intelligence</div>
    <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };</script>
  </body></html>`;

  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  return true;
}

export function downloadPestReport(report) {
  const r = report.result || {};
  const date = new Date(report.created_at || Date.now()).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const sev = (r.severity || "").toLowerCase();
  const sevColor = sev === "high" ? "#B91C1C" : sev === "medium" ? "#D97706" : "#2D6A4F";
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>AgroTerra Pest Report — ${esc(r.problem)}</title>
  <style>
    @page { margin: 18mm 16mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Helvetica, Arial, sans-serif; color: #1c1917; margin: 0; font-size: 13px; }
    .brand { display:flex; align-items:center; gap:10px; border-bottom:3px solid #78350F; padding-bottom:10px; margin-bottom:16px; }
    .logo { width:34px; height:34px; border-radius:9px; background:#78350F; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:800; }
    h1 { font-size:20px; margin:0; color:#143D29; } .sub { color:#57534e; font-size:12px; }
    .hero { background:#78350F; color:#fff; border-radius:12px; padding:16px; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    .hero h2 { margin:0 0 4px; font-size:24px; } .hero .meta { font-size:11px; text-transform:uppercase; letter-spacing:1px; opacity:.8; }
    .pill { display:inline-block; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; padding:3px 10px; border-radius:20px; color:#fff; margin-right:6px; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    .urgent { margin-top:10px; background:rgba(255,255,255,.15); border-radius:8px; padding:8px 10px; font-weight:600; }
    h3 { font-size:14px; color:#143D29; border-left:4px solid #D97706; padding-left:8px; margin:18px 0 8px; }
    .two { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
    ol, ul { margin:4px 0; padding-left:18px; } li { margin:5px 0; }
    .obs { background:#EAF6EF; border-radius:10px; padding:10px 12px; }
    .prod { border-left:2px solid #D97706; padding-left:8px; margin:6px 0; } .dose { font-family:monospace; font-size:11px; color:#78350F; }
    .foot { margin-top:22px; border-top:1px solid #e7e5e4; padding-top:8px; font-size:11px; color:#a8a29e; text-align:center; }
    .warn { font-size:11px; color:#78716c; margin-top:12px; }
  </style></head><body>
    <div class="brand"><div class="logo">A</div><div><h1>AgroTerra Pest &amp; Disease Report</h1><div class="sub">${report.crop ? esc(report.crop) + " · " : ""}${date}</div></div></div>
    <div class="hero">
      <div class="meta">${esc(r.type || "Diagnosis")}</div>
      <h2>${esc(r.problem)}</h2>
      <div><span class="pill" style="background:${sevColor}">${esc(r.severity)} severity</span><span class="pill" style="background:rgba(255,255,255,.25)">${esc(r.confidence)} confidence</span></div>
      <p style="margin:8px 0 0;opacity:.95">${esc(r.cause)}</p>
      ${r.urgency_note ? `<div class="urgent">⚠ ${esc(r.urgency_note)}</div>` : ""}
    </div>
    ${r.symptoms_seen ? `<h3>What was seen in the photo</h3><div class="obs">${esc(r.symptoms_seen)}</div>` : ""}
    ${report.notes ? `<p class="sub"><b>Farmer's notes:</b> ${esc(report.notes)}</p>` : ""}
    <div class="two">
      <div><h3>Treatment steps</h3><ol>${(r.treatments || []).map((t) => `<li><b>${esc(t.step)}</b> — ${esc(t.detail)}</li>`).join("")}</ol></div>
      <div><h3>Products to use</h3>${(r.products || []).map((p) => `<div class="prod"><b>${esc(p.name)}</b><div class="dose">${esc(p.dosage)}</div></div>`).join("")}</div>
    </div>
    <h3>Prevent it next season</h3><ul>${(r.prevention || []).map((t) => `<li>${esc(t)}</li>`).join("")}</ul>
    <p class="warn">Always read product labels and follow local regulations. This AI diagnosis is a guide — confirm with your local extension officer for severe cases.</p>
    <div class="foot">Generated by AgroTerra · AI Soil &amp; Crop Intelligence</div>
    <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };</script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  return true;
}
